import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { getUserFriendlyError } from '@/lib/errorMessages';
import AppNavbar from '@/components/app/AppNavbar';
import ImageCropper from '@/components/app/ImageCropper';
import GoodreadsImportModal from '@/components/app/GoodreadsImportModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Lock, User, Camera, BookOpen } from 'lucide-react';
import MobileBottomNav from '@/components/app/MobileBottomNav';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const settingsSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(25, 'Username must be at most 25 characters')
    .regex(/^[a-z0-9_]+$/, 'Username can only contain lowercase letters, numbers, and underscores'),
  display_name: z
    .string()
    .min(1, 'Display name is required')
    .max(60, 'Display name must be at most 60 characters'),
  bio: z.string().max(300, 'Bio must be at most 300 characters').optional(),
});

export default function SettingsPage() {
  const { user, updateProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropperOpen, setCropperOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Change password
  const [pwOpen, setPwOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Goodreads import
  const [importOpen, setImportOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      username: user?.username || '',
      display_name: user?.display_name || '',
      bio: user?.bio || '',
    },
  });

  useEffect(() => {
    if (user) {
      reset({ 
        username: user.username || '',
        display_name: user.display_name || '', 
        bio: user.bio || '' 
      });
      setAvatarPreview(user.avatar_url || '');
    }
  }, [user, reset]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropDone = (blob) => {
    setCropperOpen(false);
    setCropSrc('');
    setAvatarFile(blob);
    setAvatarPreview(URL.createObjectURL(blob));
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !user) return null;
    setUploadingAvatar(true);
    try {
      const path = `${user.id}/avatar.jpg`;
      const { error } = await supabase.storage
        .from('avatars')
        .upload(path, avatarFile, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      return `${data.publicUrl}?t=${Date.now()}`;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      let avatar_url = user.avatar_url || '';
      if (avatarFile) {
        const uploaded = await uploadAvatar();
        if (uploaded) avatar_url = uploaded;
      }
      
      // Check if username changed and validate uniqueness
      if (data.username !== user.username) {
        const { data: existing } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', data.username.toLowerCase())
          .maybeSingle();
        
        if (existing) {
          toast.error('Username already taken');
          setIsSubmitting(false);
          return;
        }
      }
      
      await updateProfile({ 
        username: data.username.toLowerCase(),
        display_name: data.display_name, 
        bio: data.bio || '', 
        avatar_url 
      });
      toast.success('Profile updated!');
      setAvatarFile(null);
      reset(data);
    } catch (err) {
      toast.error(getUserFriendlyError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated!');
      setNewPassword('');
      setConfirmPassword('');
      setPwOpen(false);
    } catch (err) {
      toast.error(getUserFriendlyError(err));
    } finally {
      setChangingPassword(false);
    }
  };

  if (!user) return null;

  const isSaving = isSubmitting || uploadingAvatar;

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <AppNavbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-6">
            <User className="w-5 h-5 text-terra" />
            <h1 className="font-heading text-2xl text-ink">Settings</h1>
          </div>

          <Card className="bg-cream border-cream-dark mb-6">
            <CardHeader className="pt-6 pb-2 px-6">
              <h2 className="font-heading text-lg text-ink">Profile</h2>
              <p className="text-sm text-ink-muted">Update your public profile information</p>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {/* Avatar upload */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative group">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={avatarPreview} alt={user.display_name} />
                    <AvatarFallback className="bg-terra text-cream text-2xl font-bold">
                      {getInitials(user.display_name || user.username)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center bg-ink/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    <Camera className="w-5 h-5 text-cream" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm font-medium text-terra hover:text-terra/80 transition-colors"
                  >
                    Change photo
                  </button>
                  <p className="text-xs text-ink-muted mt-0.5">JPG, PNG or GIF · max 5MB</p>
                  {avatarFile && (
                    <p className="text-xs text-forest mt-1">✓ {avatarFile.name} ready to upload</p>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="username" className="text-ink-light text-sm font-medium">
                    Username
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">@</span>
                    <Input
                      id="username"
                      {...register('username')}
                      className={`bg-cream-dark border-cream-dark focus:border-terra text-ink pl-7 ${errors.username ? 'border-red-400' : ''}`}
                    />
                  </div>
                  {errors.username && (
                    <p className="text-xs text-red-500">{errors.username.message}</p>
                  )}
                  <p className="text-xs text-ink-muted">Lowercase letters, numbers, and underscores only</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="display_name" className="text-ink-light text-sm font-medium">
                    Display Name
                  </Label>
                  <Input
                    id="display_name"
                    {...register('display_name')}
                    className={`bg-cream-dark border-cream-dark focus:border-terra text-ink ${errors.display_name ? 'border-red-400' : ''}`}
                  />
                  {errors.display_name && (
                    <p className="text-xs text-red-500">{errors.display_name.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="bio" className="text-ink-light text-sm font-medium">
                    Bio
                  </Label>
                  <Textarea
                    id="bio"
                    placeholder="Tell the community about yourself as a reader..."
                    {...register('bio')}
                    className={`bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted resize-none min-h-[100px] ${errors.bio ? 'border-red-400' : ''}`}
                  />
                  {errors.bio && <p className="text-xs text-red-500">{errors.bio.message}</p>}
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={isSaving || (!isDirty && !avatarFile)}
                    className="bg-terra hover:bg-terra/90 text-cream disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {uploadingAvatar ? 'Uploading...' : 'Saving...'}
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-cream border-cream-dark mb-6">
            <CardHeader className="pt-6 pb-2 px-6">
              <h2 className="font-heading text-lg text-ink">Account</h2>
            </CardHeader>
            <CardContent className="px-6 pb-6 space-y-3">
              <div>
                <p className="text-sm font-medium text-ink">Username</p>
                <p className="text-sm text-ink-muted">@{user.username}</p>
              </div>
              <Separator className="bg-cream-dark" />
              <div>
                <p className="text-sm font-medium text-ink">Email</p>
                <p className="text-sm text-ink-muted">{user.email}</p>
              </div>
            </CardContent>
          </Card>

          {/* Change password */}
          <Card className="bg-cream border-cream-dark mb-6">
            <CardContent className="px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-cream-dark flex items-center justify-center">
                    <Lock className="w-4 h-4 text-ink-muted" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">Change Password</p>
                    <p className="text-xs text-ink-muted">Update your account password</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPwOpen((v) => !v)}
                  className="border-cream-dark text-ink hover:border-terra hover:text-terra text-xs flex-shrink-0"
                >
                  {pwOpen ? 'Cancel' : 'Change'}
                </Button>
              </div>

              {pwOpen && (
                <div className="mt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-ink-light text-sm font-medium">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-ink-light text-sm font-medium">Confirm new password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className="bg-cream-dark border-cream-dark focus:border-terra text-ink placeholder:text-ink-muted text-sm"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={changingPassword || !newPassword || !confirmPassword}
                    className="bg-terra hover:bg-terra/90 text-cream text-sm"
                  >
                    {changingPassword ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Updating…</> : 'Update Password'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Goodreads import */}
          <Card className="bg-cream border-cream-dark">
            <CardContent className="px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-cream-dark flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-ink-muted" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-ink">Import from Goodreads</p>
                    <p className="text-xs text-ink-muted">Bring your reading history over via CSV export</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportOpen(true)}
                  className="border-cream-dark text-ink hover:border-terra hover:text-terra text-xs flex-shrink-0"
                >
                  Import
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <ImageCropper
        imageSrc={cropSrc}
        open={cropperOpen}
        onClose={() => { setCropperOpen(false); setCropSrc(''); }}
        onCrop={handleCropDone}
      />

      <GoodreadsImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImportDone={() => setImportOpen(false)}
      />

      <MobileBottomNav />
    </div>
  );
}
